// @ts-check
// Modo "Texto simples" (spec/email.md seção 19.2): o e-mail em 600 px com uma área de texto única e a barra fixa de
// formatação (como um e-mail do Outlook): fonte e tamanho do e-mail todo, negrito, itálico, sublinhado, tachado, cor,
// alinhamento, listas, link, imagem da biblioteca e variável. Opção do logo no topo e o rodapé automático visível.
import { html } from 'htm/preact';
import { useRef, useState } from 'preact/hooks';
import { EMAIL_FONT_STACKS } from '../../../../shared/email-design/email-document.js';
import { updateEmailStyle } from '../../../../shared/email-design/document-operations.js';
import { Icon } from '../../ui/icon.js';
import { Toggle } from '../../ui/feedback.js';
import { EmailRichTextEditor, RICH_TEXT_MODE_TOOLS } from './email-rich-text-editor.js';
import { ImageLibraryDialog } from './image-library-dialog.js';
import { emailAssetDisplayUrl, rememberEmailAssetUrls } from './local-preview.js';
import { t } from '../../i18n/index.js';

/** @typedef {import('../../../../shared/email-design/email-document.js').EmailDocument} EmailDocument */

export const RICH_TEXT_FONT_SIZES = Object.freeze([
  { key: 'small', size: 14 },
  { key: 'normal', size: 16 },
  { key: 'large', size: 18 },
]);

/** Tamanho mais próximo dos três do modo. @param {number} size */
export const richTextSizeKey = (size) => RICH_TEXT_FONT_SIZES.reduce((best, item) => (Math.abs(item.size - size) < Math.abs(best.size - size) ? item : best)).key;

/**
 * @param {{ document: EmailDocument, onChange: (document: EmailDocument, options?: { coalesceKey?: string }) => void, readOnly?: boolean,
 *   brandColor: string, logoUrl: string | null, catalog: import('../../data/email-templates.js').EmailVariableCatalog | null }} props
 */
export function RichTextMode({ document: emailDocument, onChange, readOnly = false, brandColor, logoUrl, catalog }) {
  const style = emailDocument.style;
  const insertImageRef = useRef(/** @type {((image: { url: string, alt: string, width: number | null }) => void) | null} */ (null));
  const [isLibraryOpen, setIsLibraryOpen] = useState(false);
  // o texto chega 300 ms depois da tecla: a mudança vale sobre o documento mais novo, não o daquela renderização
  const documentRef = useRef(emailDocument);
  documentRef.current = emailDocument;
  /** @param {Partial<EmailDocument['style']>} patch @param {string} [coalesceKey] */
  const changeStyle = (patch, coalesceKey) => onChange(updateEmailStyle(documentRef.current, patch), coalesceKey ? { coalesceKey } : undefined);
  const linkColor = style.linkColor === 'BRAND' ? brandColor : style.linkColor;

  return html`<div class="email-rich-text-mode">
    ${readOnly
      ? null
      : html`<div class="email-rich-text-mode-options" role="group" aria-label=${t('emailDesign.palette.style')}>
          <label class="email-inline-select">
            <span>${t('emailDesign.properties.fontFamily')}</span>
            <select class="text-input email-select" value=${style.fontFamily}
              onChange=${(/** @type {Event} */ event) => changeStyle({ fontFamily: /** @type {any} */ (/** @type {HTMLSelectElement} */ (event.currentTarget).value) })}>
              ${Object.keys(EMAIL_FONT_STACKS).map((font) => html`<option value=${font}>${t(`emailDesign.fonts.${font}`)}</option>`)}
            </select>
          </label>
          <label class="email-inline-select">
            <span>${t('emailDesign.properties.fontSize')}</span>
            <select class="text-input email-select" value=${richTextSizeKey(style.fontSize)}
              onChange=${(/** @type {Event} */ event) => {
                const key = /** @type {HTMLSelectElement} */ (event.currentTarget).value;
                const size = RICH_TEXT_FONT_SIZES.find((item) => item.key === key)?.size ?? 16;
                changeStyle({ fontSize: size });
              }}>
              ${RICH_TEXT_FONT_SIZES.map((item) => html`<option value=${item.key}>${t(`emailDesign.sizes.${item.key}`)}</option>`)}
            </select>
          </label>
          <label class="email-inline-toggle">
            <${Toggle} size="small" value=${style.showLogoHeader} ariaLabel=${t('emailDesign.properties.showLogoHeader')}
              onChange=${(/** @type {boolean} */ value) => changeStyle({ showLogoHeader: value })} />
            <span>${t('emailDesign.properties.showLogoHeader')}</span>
          </label>
        </div>`}
    <div class="email-canvas" style=${{ background: style.backgroundColor }}>
      <div class="email-canvas-content email-rich-text-page"
        style=${{ background: style.contentBackgroundColor, color: style.textColor, fontFamily: EMAIL_FONT_STACKS[style.fontFamily], fontSize: `${style.fontSize}px`, '--email-link-color': linkColor }}>
        ${style.showLogoHeader
          ? html`<div class="email-rich-text-logo">
              ${logoUrl
                ? html`<img src=${logoUrl} alt="" width="160" />`
                : html`<span class="email-canvas-image-placeholder"><${Icon} name="IconBuildingCommunity" size=${20} /><span>${t('emailDesign.logoMissing')}</span></span>`}
            </div>`
          : null}
        <${EmailRichTextEditor}
          html=${emailDocument.richText?.html ?? ''}
          catalog=${catalog}
          tools=${RICH_TEXT_MODE_TOOLS}
          toolbar="FIXED"
          readOnly=${readOnly}
          brandColor=${brandColor}
          ariaLabel=${t('emailTemplates.modes.RICH_TEXT')}
          placeholder=${t('emailDesign.richTextPlaceholder')}
          className="email-rich-text-body"
          onRequestImage=${(/** @type {(image: { url: string, alt: string, width: number | null }) => void} */ insert) => {
            insertImageRef.current = insert;
            setIsLibraryOpen(true);
          }}
          onChange=${(/** @type {string} */ value) => onChange({ ...documentRef.current, richText: { html: value } }, { coalesceKey: 'richText' })}
        />
        <p class="email-footer-notice"><${Icon} name="IconLock" size=${12} />${t('emailTemplates.editor.footerNotice')}</p>
      </div>
    </div>
    ${isLibraryOpen
      ? html`<${ImageLibraryDialog}
          onClose=${() => setIsLibraryOpen(false)}
          onSelect=${(/** @type {import('../../data/email-templates.js').EmailAsset} */ asset) => {
            rememberEmailAssetUrls([asset]);
            insertImageRef.current?.({ url: emailAssetDisplayUrl(asset), alt: '', width: asset.width });
            setIsLibraryOpen(false);
          }}
        />`
      : null}
  </div>`;
}
